import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiCheckCircle, FiClock, FiRefreshCw, FiShield } from "react-icons/fi";
import { QRCodeSVG } from "qrcode.react";
import { useParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import "./customerCreditPublic.css";

function formatMoney(value, currency = "EUR") {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "long", year: "numeric" }).format(date);
}

export default function CustomerCreditPage() {
  const { accessToken } = useParams();
  const requestRef = useRef(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());

  const loadCredit = useCallback(async (silent = false) => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    try {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError("");
      const res = await axios.get(`/api/pos/customer-credit/public/${encodeURIComponent(accessToken || "")}`);
      if (requestId !== requestRef.current) return;
      setData(res.data || null);
      setNow(Date.now());
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setData(err?.response?.data || null);
      setError(err?.response?.data?.error || "This customer credit is not available.");
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [accessToken]);

  useEffect(() => { loadCredit(false); }, [loadCredit]);

  useEffect(() => {
    if (!data?.qr_credential) return undefined;
    const refreshAfter = Math.max(20, Number(data.refresh_after_seconds || 45));
    const timer = window.setTimeout(() => loadCredit(true), refreshAfter * 1000);
    return () => window.clearTimeout(timer);
  }, [data?.qr_credential, data?.refresh_after_seconds, loadCredit]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    document.title = "Customer credit";
    return () => { document.title = "Pilcore"; };
  }, []);

  const secondsLeft = data?.qr_expires_at
    ? Math.max(0, Math.ceil((new Date(data.qr_expires_at).getTime() - now) / 1000))
    : 0;

  if (loading) {
    return <main className="customerCreditPublic"><div className="customerCreditPublicState"><FiRefreshCw className="customerCreditPublicSpin" /><span>Loading balance</span></div></main>;
  }

  if (error || !data?.account || !data?.qr_credential) {
    return <main className="customerCreditPublic"><section className="customerCreditPublicUnavailable"><FiShield /><h1>Credit unavailable</h1><p>{error}</p></section></main>;
  }

  return (
    <main className="customerCreditPublic">
      <section className="customerCreditPublicShell">
        <header className="customerCreditPublicHeader">
          <div><span>{data.business_unit_name || "Customer credit"}</span><h1>{data.account.display_name}</h1></div>
          <span className="customerCreditPublicLive"><FiCheckCircle /> Active</span>
        </header>

        <div className="customerCreditPublicBalance">
          <span>Available balance</span>
          <strong>{formatMoney(data.account.balance_amount, data.account.currency_code)}</strong>
          {data.account.expires_at ? <small>Valid until {formatDate(data.account.expires_at)}</small> : null}
        </div>

        <div className="customerCreditPublicQrArea">
          <div className="customerCreditPublicQr">
            <QRCodeSVG value={data.qr_credential} size={280} level="M" includeMargin={false} />
          </div>
          <div className="customerCreditPublicQrCopy">
            <strong>Show this QR to the waiter</strong>
            <span>The amount will be entered on the service terminal.</span>
          </div>
        </div>

        <footer className="customerCreditPublicFooter">
          <span><FiClock /> QR refreshes in {secondsLeft}s</span>
          <button type="button" onClick={() => loadCredit(true)} disabled={refreshing}><FiRefreshCw className={refreshing ? "customerCreditPublicSpin" : ""} /><span>Refresh</span></button>
        </footer>
      </section>
    </main>
  );
}
